import * as React from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MuiInput from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectSlowdownThreshold,
  setSlowdownThreshold,
} from 'state/workflowSlice';

import { InfoWalkmeCrumb } from '../info_hover/InfoHover';

import styles from 'features/task_bar/Taskbar.module.css';

const thresholdStep = 5;
const thresholdMin = 0;
const thresholdMax = 100;

const defaultSliderValue = 65;
const Input = styled(MuiInput)`
  width: 42px;
`;

export function SlowdownThresholdSlider({
  inline = false,
}: {
  inline?: boolean;
}) {
  const threshold = useAppSelector(selectSlowdownThreshold);
  const dispatch = useAppDispatch();

  function setThreshold(newValue: number) {
    dispatch(setSlowdownThreshold(newValue / 100));
  }
  const handleSliderChange = (
    event: Event,
    newValue: number | number[],
  ): void => {
    if (typeof newValue === 'number') {
      console.assert(newValue >= thresholdMin && newValue <= thresholdMax);
      setThreshold(newValue);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue =
      event.target.value === ''
        ? defaultSliderValue
        : Number(event.target.value);
    setThreshold(newValue);
  };

  const handleBlur = () => {
    if (threshold < thresholdMin) {
      setThreshold(thresholdMin);
    } else if (threshold > thresholdMax) {
      setThreshold(thresholdMax);
    }
  };

  return (
    <Box
      className={
        inline ? styles.threshold_slider_inline : styles.threshold_slider
      }
    >
      <Typography id="threshold-slider" gutterBottom>
        Slowdown Threshold:
        <InfoWalkmeCrumb elId="slowdown-threshold" />
      </Typography>
      <Grid container spacing={2} alignItems="center" width="14.6875rem">
        <Grid item xs>
          <Slider
            value={typeof threshold === 'number' ? threshold * 100 : 0}
            onChange={handleSliderChange}
            aria-labelledby="threshold-slider"
            min={thresholdMin}
            max={thresholdMax}
            step={thresholdStep}
            color="primary"
          />
        </Grid>
        <Grid item>
          <Input
            sx={{
              width: '4rem',
            }}
            color="primary"
            value={threshold * 100}
            size="small"
            onChange={handleInputChange}
            onBlur={handleBlur}
            endAdornment={
              <InputAdornment
                position="end"
                className={styles.percent_adornment}
              >
                %
              </InputAdornment>
            }
            inputProps={{
              step: thresholdStep,
              min: thresholdMin,
              max: thresholdMax,
              type: 'number',
              'aria-labelledby': 'threshold-slider',
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
